import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CoordioDropdown,
  FormField,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@coordio/ui';
import {
  Plus,
  RefreshCw,
  ArrowLeftRight,
  Unlink,
  RotateCcw,
} from 'lucide-react';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../components/dialogs/CortexDialog';
import { guardrailService, getGuardrailErrorMessage } from '../services/guardrailService';
import { agentService } from '../services/agentService';
import { safetyGateService, type SafetyGateDto } from '../services/safetyGateService';
import {
  GuardrailAction,
  GuardrailDirection,
  GuardrailMode,
  GuardrailStreamingStrategy,
  guardrailActionLabels,
  guardrailDirectionLabels,
  guardrailModeLabels,
  type GuardrailBindingDto,
  type GuardrailDto,
  type CreateGuardrailBindingRequest,
} from '../types/guardrail';
import type { AgentDefinitionDto } from '../types/agent';
import { useNotificationStore } from '../store/notificationStore';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { GuardrailsSectionNav } from '../components/guardrails/GuardrailsSectionNav';
import { PageListLayout } from '../components/layouts/PageListLayout';

const ALL_AGENTS = '__all__';

/** Violation actions offered in forms. */
const VIOLATION_ACTIONS = [
  GuardrailAction.Flag,
  GuardrailAction.Redact,
  GuardrailAction.Block,
  GuardrailAction.RequireApproval,
] as const;

/** Error actions offered in forms. */
const ERROR_ACTIONS = [GuardrailAction.Allow, GuardrailAction.Flag, GuardrailAction.Block] as const;

interface BindFormState {
  guardrailKey: string;
  agentDefinitionId: string; // ALL_AGENTS sentinel = every agent in scope
  direction: number;
  mode: number;
  approvedBy: string;
  violationAction: number;
  errorAction: number;
  agentSafetyGateId: string;
  timeoutMs: number;
  changeReason: string;
}

const emptyBindForm = (): BindFormState => ({
  guardrailKey: '',
  agentDefinitionId: ALL_AGENTS,
  direction: GuardrailDirection.UserInput,
  mode: GuardrailMode.Shadow,
  approvedBy: '',
  violationAction: GuardrailAction.Flag,
  errorAction: GuardrailAction.Block,
  agentSafetyGateId: '',
  timeoutMs: 250,
  changeReason: '',
});

/**
 * Guardrail bindings page: which guardrails apply to which agents, in which mode.
 */
export const GuardrailBindingsPage = () => {
  const { addNotification } = useNotificationStore();

  const [bindings, setBindings] = useState<GuardrailBindingDto[]>([]);
  const [guardrails, setGuardrails] = useState<GuardrailDto[]>([]);
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [safetyGates, setSafetyGates] = useState<SafetyGateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bind dialog
  const [bindOpen, setBindOpen] = useState(false);
  const [bindForm, setBindForm] = useState<BindFormState>(emptyBindForm());
  const [binding, setBinding] = useState(false);

  // Set mode dialog
  const [modeTarget, setModeTarget] = useState<GuardrailBindingDto | null>(null);
  const [modeForm, setModeForm] = useState({ mode: GuardrailMode.Shadow as number, approvedBy: '', changeReason: '' });
  const [settingMode, setSettingMode] = useState(false);

  // Deactivate dialog
  const [deactivateTarget, setDeactivateTarget] = useState<GuardrailBindingDto | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  // Reactivate dialog
  const [reactivateTarget, setReactivateTarget] = useState<GuardrailBindingDto | null>(null);
  const [reactivateForm, setReactivateForm] = useState({ approvedBy: '', changeReason: '' });
  const [reactivating, setReactivating] = useState(false);

  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    agents.forEach((agent) => map.set(agent.id, agent.name));
    return map;
  }, [agents]);

  const loadBindings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await guardrailService.getBindings();
      setBindings(data);
    } catch (err: unknown) {
      const message = getGuardrailErrorMessage(err, 'Failed to load guardrail bindings');
      setError(message);
      addNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPickerData = async () => {
    // Picker data failures should not block the bindings table itself.
    try {
      const [guardrailList, agentPage, gatePage] = await Promise.all([
        guardrailService.getGuardrails(),
        agentService.getAgents({ pageNumber: 1, pageSize: 200 }),
        safetyGateService.getSafetyGates({ pageNumber: 1, pageSize: 100, isActive: true }),
      ]);
      setGuardrails(guardrailList);
      setAgents(agentPage.items);
      setSafetyGates(gatePage.items);
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to load picker data'), 'error');
    }
  };

  useEffect(() => {
    loadBindings();
    loadPickerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const agentLabel = (agentDefinitionId?: string | null) => {
    if (!agentDefinitionId) return 'All agents';
    return agentNameById.get(agentDefinitionId) ?? agentDefinitionId;
  };

  const bindValid =
    bindForm.guardrailKey !== '' &&
    (bindForm.mode !== GuardrailMode.Enforce || bindForm.approvedBy.trim() !== '') &&
    (bindForm.violationAction !== GuardrailAction.RequireApproval || bindForm.agentSafetyGateId !== '');

  const handleBind = async () => {
    const body: CreateGuardrailBindingRequest = {
      guardrailKey: bindForm.guardrailKey,
      tracksCurrent: true,
      agentDefinitionId: bindForm.agentDefinitionId === ALL_AGENTS ? undefined : bindForm.agentDefinitionId,
      direction: bindForm.direction,
      mode: bindForm.mode,
      executionOrder: 100,
      violationAction: bindForm.violationAction,
      inconclusiveAction: GuardrailAction.Flag,
      errorAction: bindForm.errorAction,
      severity: 'Medium',
      timeoutMs: bindForm.timeoutMs,
      streamingStrategy: GuardrailStreamingStrategy.BufferFull,
      isNonSuppressible: false,
      exemptionsAllowed: false,
      approvedBy: bindForm.mode === GuardrailMode.Enforce ? bindForm.approvedBy.trim() : undefined,
      agentSafetyGateId:
        bindForm.violationAction === GuardrailAction.RequireApproval && bindForm.agentSafetyGateId
          ? bindForm.agentSafetyGateId
          : undefined,
      changeReason: bindForm.changeReason.trim() || undefined,
    };

    setBinding(true);
    try {
      await guardrailService.createBinding(body);
      addNotification('Guardrail bound successfully', 'success');
      setBindOpen(false);
      loadBindings();
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to bind guardrail'), 'error');
    } finally {
      setBinding(false);
    }
  };

  const handleOpenSetMode = (row: GuardrailBindingDto) => {
    setModeForm({
      mode: row.mode === GuardrailMode.Shadow ? GuardrailMode.Enforce : GuardrailMode.Shadow,
      approvedBy: '',
      changeReason: '',
    });
    setModeTarget(row);
  };

  const setModeValid =
    modeForm.changeReason.trim() !== '' &&
    (modeForm.mode !== GuardrailMode.Enforce || modeForm.approvedBy.trim() !== '');

  const handleSetMode = async () => {
    if (!modeTarget) return;
    setSettingMode(true);
    try {
      await guardrailService.setBindingMode(modeTarget.bindingKey, {
        mode: modeForm.mode,
        approvedBy: modeForm.mode === GuardrailMode.Enforce ? modeForm.approvedBy.trim() : undefined,
        changeReason: modeForm.changeReason.trim(),
      });
      addNotification(`Binding switched to ${guardrailModeLabels[modeForm.mode]}`, 'success');
      setModeTarget(null);
      loadBindings();
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to set binding mode'), 'error');
    } finally {
      setSettingMode(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget || !deactivateReason.trim()) return;
    setDeactivating(true);
    try {
      await guardrailService.deactivateBinding(deactivateTarget.bindingKey, {
        changeReason: deactivateReason.trim(),
      });
      addNotification(`Binding ${deactivateTarget.bindingKey} deactivated`, 'success');
      setDeactivateTarget(null);
      setDeactivateReason('');
      loadBindings();
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to deactivate binding'), 'error');
    } finally {
      setDeactivating(false);
    }
  };

  const isBindingLive = (row: GuardrailBindingDto) => row.isActive && row.isCurrent;

  const handleOpenReactivate = (row: GuardrailBindingDto) => {
    setReactivateForm({ approvedBy: '', changeReason: '' });
    setReactivateTarget(row);
  };

  const reactivateValid =
    reactivateForm.changeReason.trim() !== '' &&
    (reactivateTarget?.mode !== GuardrailMode.Enforce || reactivateForm.approvedBy.trim() !== '');

  const handleReactivate = async () => {
    if (!reactivateTarget || !reactivateValid) return;
    setReactivating(true);
    try {
      await guardrailService.reactivateBinding(reactivateTarget.bindingKey, {
        changeReason: reactivateForm.changeReason.trim(),
        approvedBy:
          reactivateTarget.mode === GuardrailMode.Enforce ? reactivateForm.approvedBy.trim() : undefined,
      });
      addNotification(`Binding ${reactivateTarget.bindingKey} reactivated`, 'success');
      setReactivateTarget(null);
      loadBindings();
    } catch (err: unknown) {
      addNotification(getGuardrailErrorMessage(err, 'Failed to reactivate binding'), 'error');
    } finally {
      setReactivating(false);
    }
  };

  const tableColumns: DataTableColumn<GuardrailBindingDto>[] = [
    {
      id: 'bindingKey',
      label: 'Binding Key',
      minWidth: 200,
      render: (row) => (
        <span className="text-sm font-mono">{row.bindingKey}</span>
      ),
    },
    {
      id: 'guardrailKey',
      label: 'Guardrail',
      minWidth: 140,
      render: (row) => (
        <span className="text-sm font-mono">{row.guardrailKey}</span>
      ),
    },
    {
      id: 'agent',
      label: 'Agent',
      minWidth: 140,
      render: (row) => agentLabel(row.agentDefinitionId),
    },
    {
      id: 'direction',
      label: 'Direction',
      minWidth: 110,
      render: (row) => guardrailDirectionLabels[row.direction] ?? row.direction,
    },
    {
      id: 'mode',
      label: 'Mode',
      minWidth: 100,
      render: (row) => (
        <Chip
          label={guardrailModeLabels[row.mode] ?? row.mode}
          color={row.mode === GuardrailMode.Enforce ? 'red' : 'blue'}
          className="text-xs"
        />
      ),
    },
    {
      id: 'violationAction',
      label: 'Violation Action',
      minWidth: 130,
      render: (row) => guardrailActionLabels[row.violationAction] ?? row.violationAction,
    },
    { id: 'revision', label: 'Revision', minWidth: 80, render: (row) => `r${row.revision}` },
    {
      id: 'isActive',
      label: 'Active',
      minWidth: 90,
      render: (row) =>
        row.isActive ? <Chip label="Active" color="green" className="text-xs" /> : <Chip label="Inactive" className="text-xs" />,
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'right',
      render: (row) =>
        isBindingLive(row) ? (
          <div className="flex justify-end gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => handleOpenSetMode(row)}>
                    <ArrowLeftRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Set mode (Shadow ↔ Enforce)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => setDeactivateTarget(row)}>
                    <Unlink className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deactivate binding</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="text-green-600 hover:text-green-700" onClick={() => handleOpenReactivate(row)}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reactivate binding</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
    },
  ];

  return (
    <>
      <PageListLayout
        title="Guardrail Bindings"
        subtitle="Which guardrails apply to which agents, at which pipeline stage, and in which mode."
        createButton={{
          label: 'Bind Guardrail',
          onClick: () => {
            setBindForm(emptyBindForm());
            setBindOpen(true);
          },
          icon: <Plus />,
        }}
        headerActions={
          <Button variant="outline" onClick={loadBindings} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        isEmpty={!loading && bindings.length === 0}
        emptyState={{
          title: 'No guardrail bindings',
          description: 'Bind a guardrail to an agent (or all agents) to start evaluating turns.',
          action: {
            label: 'Bind Guardrail',
            onClick: () => {
              setBindForm(emptyBindForm());
              setBindOpen(true);
            },
          },
        }}
      >
        <GuardrailsSectionNav />
        <DataTable columns={tableColumns} rows={bindings} />
      </PageListLayout>

      {/* Bind Guardrail Dialog */}
      <CortexDialog open={bindOpen} onClose={() => setBindOpen(false)} title="Bind Guardrail" size="md">
        <CortexDialogBody>
          <div className="flex flex-col gap-5 pt-1">
            <FormField label="Guardrail" required>
              <CoordioDropdown
                options={guardrails.map((g) => ({
                  value: g.guardrailKey,
                  label: `${g.guardrailKey} — ${g.name} (v${g.version})`,
                }))}
                value={bindForm.guardrailKey || null}
                onChange={(val) => setBindForm({ ...bindForm, guardrailKey: val ?? '' })}
              />
            </FormField>
            <FormField label="Agent">
              <CoordioDropdown
                options={[
                  { value: ALL_AGENTS, label: 'All agents' },
                  ...agents.map((agent) => ({ value: agent.id, label: agent.name })),
                ]}
                value={bindForm.agentDefinitionId}
                onChange={(val) => setBindForm({ ...bindForm, agentDefinitionId: val ?? ALL_AGENTS })}
              />
            </FormField>
            <div className="flex gap-3">
              <div className="flex-1">
                <FormField label="Direction">
                  <CoordioDropdown
                    options={[
                      { value: GuardrailDirection.UserInput.toString(), label: 'User input' },
                      { value: GuardrailDirection.AgentOutput.toString(), label: 'Agent output' },
                    ]}
                    value={bindForm.direction.toString()}
                    onChange={(val) => setBindForm({ ...bindForm, direction: Number(val) })}
                  />
                </FormField>
              </div>
              <div className="flex-1">
                <FormField label="Mode">
                  <CoordioDropdown
                    options={[
                      { value: GuardrailMode.Shadow.toString(), label: 'Shadow' },
                      { value: GuardrailMode.Enforce.toString(), label: 'Enforce' },
                    ]}
                    value={bindForm.mode.toString()}
                    onChange={(val) => setBindForm({ ...bindForm, mode: Number(val) })}
                  />
                </FormField>
              </div>
            </div>
            {bindForm.mode === GuardrailMode.Enforce && (
              <FormField label="Approved By" required description="Enforce mode requires an approver">
                <Input
                  className="w-full"
                  value={bindForm.approvedBy}
                  onChange={(e) => setBindForm({ ...bindForm, approvedBy: e.target.value })}
                />
              </FormField>
            )}
            <div className="flex gap-3">
              <div className="flex-1">
                <FormField label="Violation Action">
                  <CoordioDropdown
                    options={VIOLATION_ACTIONS.map((action) => ({
                      value: action.toString(),
                      label: guardrailActionLabels[action],
                    }))}
                    value={bindForm.violationAction.toString()}
                    onChange={(val) => setBindForm({ ...bindForm, violationAction: Number(val) })}
                  />
                </FormField>
              </div>
              <div className="flex-1">
                <FormField label="Error Action">
                  <CoordioDropdown
                    options={ERROR_ACTIONS.map((action) => ({
                      value: action.toString(),
                      label: guardrailActionLabels[action],
                    }))}
                    value={bindForm.errorAction.toString()}
                    onChange={(val) => setBindForm({ ...bindForm, errorAction: Number(val) })}
                  />
                </FormField>
              </div>
            </div>
            {bindForm.violationAction === GuardrailAction.RequireApproval && (
              <FormField label="Safety Gate" required>
                <CoordioDropdown
                  options={safetyGates.map((gate) => ({
                    value: gate.id,
                    label: gate.name,
                  }))}
                  value={bindForm.agentSafetyGateId || null}
                  onChange={(val) => setBindForm({ ...bindForm, agentSafetyGateId: val ?? '' })}
                />
              </FormField>
            )}
            <FormField label="Timeout (ms)">
              <Input
                className="w-40"
                type="number"
                value={bindForm.timeoutMs}
                onChange={(e) => setBindForm({ ...bindForm, timeoutMs: Number(e.target.value) || 0 })}
              />
            </FormField>
            <FormField label="Change Reason">
              <Input
                className="w-full"
                value={bindForm.changeReason}
                onChange={(e) => setBindForm({ ...bindForm, changeReason: e.target.value })}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="ghost" onClick={() => setBindOpen(false)}>Cancel</Button>
          <Button onClick={handleBind} disabled={!bindValid || binding}>
            Bind
          </Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* Set Mode Dialog */}
      <CortexDialog open={!!modeTarget} onClose={() => setModeTarget(null)} title={`Set Mode — ${modeTarget?.bindingKey ?? ''}`} size="md">
        <CortexDialogBody>
          <div className="flex flex-col gap-5 pt-1">
            <FormField label="Mode">
              <CoordioDropdown
                options={[
                  { value: GuardrailMode.Shadow.toString(), label: 'Shadow' },
                  { value: GuardrailMode.Enforce.toString(), label: 'Enforce' },
                ]}
                value={modeForm.mode.toString()}
                onChange={(val) => setModeForm({ ...modeForm, mode: Number(val) })}
              />
            </FormField>
            {modeForm.mode === GuardrailMode.Enforce && (
              <FormField label="Approved By" required description="Switching to Enforce requires an approver">
                <Input
                  className="w-full"
                  value={modeForm.approvedBy}
                  onChange={(e) => setModeForm({ ...modeForm, approvedBy: e.target.value })}
                />
              </FormField>
            )}
            <FormField label="Change Reason" required>
              <Input
                className="w-full"
                value={modeForm.changeReason}
                onChange={(e) => setModeForm({ ...modeForm, changeReason: e.target.value })}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="ghost" onClick={() => setModeTarget(null)}>Cancel</Button>
          <Button onClick={handleSetMode} disabled={!setModeValid || settingMode}>
            Set Mode
          </Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* Deactivate Dialog */}
      <CortexDialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} title={`Deactivate — ${deactivateTarget?.bindingKey ?? ''}`} size="md">
        <CortexDialogBody>
          <div className="flex flex-col gap-4 pt-1">
            <span className="text-sm text-[var(--color-text-secondary)]">
              This binding will stop running on all future turns. Execution history is kept. Use
              <strong> Reactivate</strong> on the row to turn it back on with the same settings.
            </span>
            <FormField label="Change Reason" required>
              <Input
                className="w-full"
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="ghost" onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleDeactivate}
            disabled={deactivating || !deactivateReason.trim()}
          >
            Deactivate
          </Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* Reactivate Dialog */}
      <CortexDialog open={!!reactivateTarget} onClose={() => setReactivateTarget(null)} title={`Reactivate — ${reactivateTarget?.bindingKey ?? ''}`} size="md">
        <CortexDialogBody>
          <div className="flex flex-col gap-4 pt-1">
            <span className="text-sm text-[var(--color-text-secondary)]">
              Restores this binding with the same guardrail, agent, direction, mode, and actions as before
              deactivation.
            </span>
            {reactivateTarget && (
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-[var(--color-bg-inset,#fafafa)] p-3 text-sm">
                <span className="text-xs text-[var(--color-text-secondary)]">Guardrail</span>
                <span className="font-mono">{reactivateTarget.guardrailKey}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">Agent</span>
                <span>{agentLabel(reactivateTarget.agentDefinitionId)}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">Direction</span>
                <span>{guardrailDirectionLabels[reactivateTarget.direction]}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">Mode</span>
                <span>{guardrailModeLabels[reactivateTarget.mode]}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">Violation</span>
                <span>{guardrailActionLabels[reactivateTarget.violationAction]}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">Error</span>
                <span>{guardrailActionLabels[reactivateTarget.errorAction]}</span>
              </div>
            )}
            {reactivateTarget?.mode === GuardrailMode.Enforce && (
              <FormField label="Approved By" required description="Enforce mode requires an approver">
                <Input
                  className="w-full"
                  value={reactivateForm.approvedBy}
                  onChange={(e) => setReactivateForm({ ...reactivateForm, approvedBy: e.target.value })}
                />
              </FormField>
            )}
            <FormField label="Change Reason" required>
              <Input
                className="w-full"
                value={reactivateForm.changeReason}
                onChange={(e) => setReactivateForm({ ...reactivateForm, changeReason: e.target.value })}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="ghost" onClick={() => setReactivateTarget(null)}>Cancel</Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handleReactivate}
            disabled={reactivating || !reactivateValid}
          >
            Reactivate
          </Button>
        </CortexDialogFooter>
      </CortexDialog>
    </>
  );
};
